
import React, { useState, useEffect } from 'react';
import { Building2, Mail, CheckCircle2, Plus, Loader2, LogOut, RefreshCw, Shield, Star, Check, Lock, Clock, Send, CreditCard, QrCode, Barcode, Copy, Download, ExternalLink, Zap, ArrowRightCircle, AlertCircle, ChevronRight, Settings2, RefreshCcw, Sparkles } from 'lucide-react';
import { supabase } from '../supabase';
import { User, Invitation, UserRole, PLAN_LIMITS, EmailOverride } from '../types';
import PaymentStep from './PaymentStep';

interface OnboardingProps {
  currentUser: User;
  onRefresh: () => void;
}

type PlanType = 'essential' | 'advanced' | 'pro';
type Step = 'options' | 'create_cabinet' | 'select_plan' | 'payment' | 'payment_result' | 'success_pending' | 'pending_approval' | 'override_processing';

const Onboarding: React.FC<OnboardingProps> = ({ currentUser, onRefresh }) => {
  const [step, setStep] = useState<Step>('options');
  const [cabinetName, setCabinetName] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('advanced'); // Pré-selecionado Avançado
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly'); // Default Anual
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [checkingInvites, setCheckingInvites] = useState(true);
  
  const [hasOverride, setHasOverride] = useState<EmailOverride | null>(null);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [fetchingPayment, setFetchingPayment] = useState(false);

  const pendingRequest = currentUser.settings?.pending_cabinet_request;

  useEffect(() => {
    fetchInvitations();
    checkEmailOverride();
    
    if (pendingRequest) {
      setCabinetName(pendingRequest.name);
      setSelectedPlan(pendingRequest.plan);
      setBillingCycle(pendingRequest.billing_cycle || 'yearly');
      
      if (pendingRequest.is_override) {
        setStep('success_pending');
      } else {
        setStep('success_pending');
        if (pendingRequest.payment_id) {
          fetchExistingPayment(pendingRequest.payment_id);
        }
      }
    }
  }, [currentUser]);

  const fetchExistingPayment = async (paymentId: string) => {
    setFetchingPayment(true);
    try {
      const { data: payRes } = await supabase.functions.invoke('asaas-proxy', {
        body: { endpoint: `/payments/${paymentId}`, method: 'GET' }
      });

      let pixData = null;
      if (payRes?.ok && payRes.billingType === 'PIX') {
        const { data: pixRes } = await supabase.functions.invoke('asaas-proxy', {
          body: { endpoint: `/payments/${paymentId}/pixQrCode`, method: 'GET' }
        });
        if (pixRes.ok) pixData = pixRes;
      }

      if (payRes?.ok) {
        setPaymentData({
          payment: payRes,
          pix: pixData,
          billingInfo: pendingRequest?.billing_info
        });
      }
    } catch (e) {
      console.error("Erro ao buscar dados do pagamento:", e);
    } finally {
      setFetchingPayment(false);
    }
  };

  const checkEmailOverride = async () => {
    try {
      const { data } = await supabase.from('email_overrides').select('*').eq('email', currentUser.email.toLowerCase().trim()).maybeSingle();
      if (data) setHasOverride(data);
    } catch (e) {}
  };

  const fetchInvitations = async () => {
    setCheckingInvites(true);
    try {
      const { data, error } = await supabase.from('invitations').select('*').eq('email', currentUser.email);
      if (error) throw error;
      const invites = data || [];
      setInvitations(invites);
      if (invites.some(i => i.status === 'request')) setStep('pending_approval');
    } catch (err) { console.error(err); } finally { setCheckingInvites(false); }
  };

  const handleProcessOverride = async () => {
    if (!hasOverride) return;
    setStep('override_processing');
    setLoadingCreate(true);
    try {
      const slug = cabinetName.toLowerCase().replace(/\s+/g, '-') + '-' + Math.random().toString(36).substring(2, 6);
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + hasOverride.duration_days);

      const pendingRequestData = {
        name: cabinetName,
        slug: slug,
        plan: 'essential' as PlanType,
        billing_cycle: 'monthly' as const,
        requested_at: new Date().toISOString(),
        is_override: true,
        expires_at: expirationDate.toISOString()
      };

      const { error: profileError } = await supabase.from('profiles').update({
        settings: { 
          ...currentUser.settings,
          pending_cabinet_request: pendingRequestData 
        }
      }).eq('id', currentUser.id);

      if (profileError) throw profileError;

      await supabase.from('email_overrides').delete().eq('id', hasOverride.id);
      setStep('success_pending');
    } catch (err: any) {
      alert("Erro no override: " + err.message);
      setStep('select_plan');
    } finally {
      setLoadingCreate(false);
    }
  };

  const handlePaymentSuccess = async (asaasResult: any) => {
    setPaymentData(asaasResult);
    setLoadingCreate(true);
    try {
      const slug = cabinetName.toLowerCase().replace(/\s+/g, '-') + '-' + Math.random().toString(36).substring(2, 6);
      
      const pendingRequestData = {
        name: cabinetName,
        slug: slug,
        plan: selectedPlan,
        billing_cycle: billingCycle,
        requested_at: new Date().toISOString(),
        billing_info: asaasResult.billingInfo,
        asaas_customer_id: asaasResult.customer.id,
        payment_id: asaasResult.payment.id
      };

      const { error: profileError } = await supabase.from('profiles').update({
        settings: { 
          ...currentUser.settings,
          pending_cabinet_request: pendingRequestData 
        }
      }).eq('id', currentUser.id);

      if (profileError) throw profileError;

      if (asaasResult.payment.billingType === 'CREDIT_CARD' && (asaasResult.payment.status === 'CONFIRMED' || asaasResult.payment.status === 'RECEIVED')) {
         setStep('success_pending');
      } else {
         setStep('payment_result');
      }
    } catch (err: any) {
      alert("Erro ao salvar solicitação: " + err.message);
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleAcceptInvite = async (invite: Invitation) => {
    setLoadingInvite(true);
    try {
      const { error: profileError } = await supabase.from('profiles').update({ container_id: invite.container_id, role: invite.role }).eq('id', currentUser.id);
      if (profileError) throw profileError;
      await supabase.from('invitations').delete().eq('id', invite.id);
      onRefresh();
    } catch (err: any) {
      alert("Erro ao aceitar convite: " + err.message);
    } finally {
      setLoadingInvite(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  if (step === 'override_processing') {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-navy-950 flex flex-col items-center justify-center p-6 animate-in fade-in">
         <Loader2 className="animate-spin text-parliament-600 mb-6" size={64} />
         <h2 className="text-3xl font-black text-navy-950 dark:text-white">Validando seu Acesso...</h2>
         <p className="text-slate-500 font-medium mt-2">Ativando sua conta de cortesia.</p>
      </div>
    );
  }

  if (step === 'payment') {
    return <PaymentStep plan={selectedPlan} billingCycle={billingCycle} onBack={() => setStep('select_plan')} onSuccess={handlePaymentSuccess} />;
  }

  if (step === 'payment_result' && paymentData) {
    const isPix = paymentData.payment.billingType === 'PIX';
    const isBoleto = paymentData.payment.billingType === 'BOLETO';
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-navy-950 flex flex-col items-center justify-center p-6 animate-in fade-in">
         <div className="bg-white dark:bg-navy-900 p-12 rounded-[40px] shadow-2xl max-w-2xl w-full text-center border border-neutral-100 dark:border-navy-800">
            <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8"><CheckCircle2 size={40} /></div>
            <h2 className="text-3xl font-black text-navy-950 dark:text-white mb-2">Solicitação Recebida!</h2>
            <p className="text-slate-500 mb-10">Aguardamos a confirmação do seu primeiro pagamento para ativar o gabinete.</p>
            {isPix && paymentData.pix && (
               <div className="space-y-6">
                  <div className="bg-slate-50 dark:bg-navy-950 p-6 rounded-3xl inline-block border-2 border-parliament-600"><img src={`data:image/png;base64,${paymentData.pix.encodedImage}`} alt="QR Code PIX" className="w-48 h-48 mx-auto" /></div>
                  <div className="space-y-2"><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Copia e Cola</p><div className="flex gap-2"><input readOnly value={paymentData.pix.payload} className="flex-1 bg-slate-50 dark:bg-navy-950 border border-neutral-200 rounded-xl px-4 py-3 text-[10px] font-mono dark:text-white truncate" /><button onClick={() => navigator.clipboard.writeText(paymentData.pix.payload)} className="p-3 bg-navy-950 text-white rounded-xl"><Copy size={16}/></button></div></div>
               </div>
            )}
            {isBoleto && (
               <div className="space-y-6"><div className="p-8 bg-parliament-50 dark:bg-parliament-900/20 rounded-3xl border border-parliament-100 text-center"><Barcode size={48} className="mx-auto text-parliament-600 mb-4" /><p className="text-sm font-bold text-parliament-900 dark:text-parliament-100">Boleto gerado com sucesso!</p><a href={paymentData.payment.bankSlipUrl} target="_blank" className="mt-4 inline-flex items-center gap-2 bg-parliament-600 text-white px-8 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg"><Download size={14} /> Baixar Boleto</a></div></div>
            )}
            <div className="mt-12 pt-8 border-t border-neutral-100 dark:border-navy-800"><p className="text-xs text-slate-400 font-medium mb-6">Assim que o pagamento for compensado, sua conta será ativada automaticamente.</p><button onClick={() => setStep('success_pending')} className="w-full py-4 bg-navy-950 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest">Continuar para o Status</button></div>
         </div>
      </div>
    );
  }

  if (step === 'success_pending') {
    const isOverride = pendingRequest?.is_override;
    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-navy-950 flex flex-col items-center justify-center p-6 animate-in fade-in">
           <div className="bg-white dark:bg-navy-900 p-12 rounded-[40px] shadow-2xl max-w-2xl w-full text-center border border-neutral-100 dark:border-navy-800">
              {isOverride ? (
                <>
                  <div className="w-20 h-20 bg-amber-50 dark:bg-amber-900/20 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce"><Clock size={40} /></div>
                  <h2 className="text-4xl font-black text-navy-950 dark:text-white tracking-tighter mb-4 uppercase">Aguardando Ativação</h2>
                  <p className="text-slate-500 text-lg font-medium leading-relaxed max-w-md mx-auto mb-10">
                     Sua solicitação de cortesia para o gabinete <span className="text-navy-950 dark:text-white font-bold">{cabinetName}</span> foi registrada. 
                     Nossa equipe está processando a liberação.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-full flex items-center justify-center mx-auto mb-8"><AlertCircle size={40} /></div>
                  <h2 className="text-4xl font-black text-navy-950 dark:text-white tracking-tighter mb-4 uppercase leading-none">Pagamento ainda não identificado</h2>
                  <p className="text-slate-500 text-lg font-medium leading-relaxed max-w-md mx-auto mb-10 mt-6">
                     Sua solicitação para o gabinete <span className="text-navy-950 dark:text-white font-bold">{cabinetName}</span> está registrada, mas ainda não identificamos o pagamento do plano.
                  </p>
                  
                  {fetchingPayment ? (
                    <div className="p-10 flex flex-col items-center gap-3">
                       <Loader2 className="animate-spin text-parliament-600" />
                       <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Buscando dados de pagamento...</p>
                    </div>
                  ) : paymentData ? (
                    <div className="mb-8 p-6 bg-slate-50 dark:bg-navy-950 rounded-3xl border-2 border-parliament-100 dark:border-navy-800 text-left space-y-6">
                       <div className="space-y-4">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Opções de Pagamento Geradas:</p>
                          <div className="flex flex-col gap-3">
                              {paymentData.pix && (
                                <button onClick={() => setStep('payment_result')} className="w-full flex items-center justify-between p-5 bg-white dark:bg-navy-900 rounded-2xl shadow-sm border border-neutral-100 dark:border-navy-700 hover:border-emerald-400 transition-all group">
                                  <div className="flex items-center gap-3"><div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl group-hover:scale-110 transition-transform"><QrCode size={20}/></div><span className="text-sm font-bold text-navy-900 dark:text-white">Ver QR Code PIX</span></div>
                                  <ChevronRight size={16} className="text-slate-300"/>
                                </button>
                              )}
                              {paymentData.payment?.bankSlipUrl && (
                                <a href={paymentData.payment.bankSlipUrl} target="_blank" className="w-full flex items-center justify-between p-5 bg-white dark:bg-navy-900 rounded-2xl shadow-sm border border-neutral-100 dark:border-navy-700 hover:border-blue-400 transition-all group">
                                  <div className="flex items-center gap-3"><div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl group-hover:scale-110 transition-transform"><Barcode size={20}/></div><span className="text-sm font-bold text-navy-900 dark:text-white">Baixar Boleto Atual</span></div>
                                  <Download size={16} className="text-slate-300"/>
                                </a>
                              )}
                          </div>
                       </div>
                       
                       <div className="pt-6 border-t border-slate-200 dark:border-navy-800">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Deseja outro método?</p>
                          <button onClick={() => setStep('select_plan')} className="w-full py-4 bg-navy-950 dark:bg-parliament-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl flex items-center justify-center gap-2 hover:scale-[1.02] transition-all">
                             <CreditCard size={18} /> ALTERAR MÉTODO DE PAGAMENTO
                          </button>
                       </div>
                    </div>
                  ) : (
                    <button onClick={() => setStep('select_plan')} className="mb-8 w-full py-5 bg-parliament-600 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-xl flex items-center justify-center gap-2 hover:scale-[1.02] transition-all">
                       <CreditCard size={18} /> REALIZAR PAGAMENTO AGORA
                    </button>
                  )}
                </>
              )}

              <div className="p-6 bg-neutral-50 dark:bg-navy-950 rounded-3xl border border-neutral-100 dark:border-navy-800 mb-8">
                 <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">O que acontece agora?</p>
                 <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mt-2">Assim que ativado, você receberá um e-mail e ao atualizar esta página terá acesso total ao painel.</p>
              </div>
              
              <div className="flex gap-4">
                 <button onClick={handleLogout} className="flex-1 py-4 text-slate-400 font-black text-[10px] uppercase tracking-widest hover:text-navy-950">Sair da Conta</button>
                 <button onClick={() => window.location.reload()} className="flex-[2] bg-navy-950 dark:bg-white text-white dark:text-navy-950 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"><RefreshCw size={14}/> Verificar Novamente</button>
              </div>
           </div>
        </div>
    );
  }

  if (step === 'select_plan') {
    const plansList = [
      { 
        id: 'essential' as PlanType, 
        label: 'Essencial', 
        priceM: PLAN_LIMITS.essential.priceMonthly, 
        priceY: PLAN_LIMITS.essential.priceYearly, 
        installment: PLAN_LIMITS.essential.installmentYearly,
        features: [`Até ${PLAN_LIMITS.essential.contacts.toLocaleString()} Contatos`, 'Até 3 assessores', '5GB Armazenamento', 'Gestão Completa', 'Mapa Estratégico'] 
      },
      { 
        id: 'advanced' as PlanType, 
        label: 'Avançado', 
        priceM: PLAN_LIMITS.advanced.priceMonthly, 
        priceY: PLAN_LIMITS.advanced.priceYearly, 
        installment: PLAN_LIMITS.advanced.installmentYearly,
        features: [`Até ${PLAN_LIMITS.advanced.contacts.toLocaleString()} Contatos`, 'Sem limite de assessores', 'Inclua lideranças externas', '50GB Armazenamento', 'Gestão Completa', 'Mapa Estratégico'],
        recommended: true
      },
      { 
        id: 'pro' as PlanType, 
        label: 'Pro', 
        priceM: PLAN_LIMITS.pro.priceMonthly, 
        priceY: PLAN_LIMITS.pro.priceYearly, 
        installment: PLAN_LIMITS.pro.installmentYearly,
        features: ['Contatos Ilimitados', 'Equipe Ilimitada', '100GB Armazenamento', 'Gestão Completa', 'Mapa Estratégico'] 
      },
    ];

    return (
      <div className="min-h-screen bg-navy-950 flex flex-col items-center justify-center p-6 animate-in slide-in-from-right-4 relative overflow-hidden">
         {/* Efeitos de Fundo para o Visual Profissional */}
         <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-navy-900 via-navy-950 to-black pointer-events-none"></div>
         <div className="absolute -top-24 -left-24 w-96 h-96 bg-parliament-600/10 rounded-full blur-[120px] pointer-events-none"></div>
         <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-emerald-600/10 rounded-full blur-[120px] pointer-events-none"></div>

         <div className="max-w-6xl w-full relative z-10">
            <div className="mb-16 text-center">
              <h2 className="text-6xl font-black text-white mb-6 tracking-tighter">Escolha seu Plano</h2>
              <div className="flex items-center justify-center gap-8">
                 <button onClick={() => setBillingCycle('monthly')} className={`text-xl font-bold transition-all ${billingCycle === 'monthly' ? 'text-white scale-110' : 'text-slate-500 hover:text-slate-400'}`}>Mensal</button>
                 <div onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')} className="w-20 h-10 bg-navy-800 rounded-full relative cursor-pointer p-1.5 border border-white/10 shadow-inner">
                    <div className={`w-6 h-6 bg-parliament-500 rounded-full shadow-[0_0_15px_rgba(0,132,255,0.5)] transition-all duration-500 ${billingCycle === 'yearly' ? 'translate-x-10' : 'translate-x-0'}`}></div>
                 </div>
                 <button onClick={() => setBillingCycle('yearly')} className={`text-xl font-bold transition-all flex items-center gap-3 ${billingCycle === 'yearly' ? 'text-parliament-400 scale-110' : 'text-slate-500 hover:text-slate-400'}`}>
                    Anual <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-4 py-1.5 rounded-full font-black uppercase tracking-widest border border-emerald-500/30">ECONOMIZE 30%</span>
                 </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 items-stretch">
              {plansList.map(plan => {
                const isActive = selectedPlan === plan.id;
                return (
                  <div 
                    key={plan.id} 
                    onClick={() => setSelectedPlan(plan.id)} 
                    className={`group relative p-10 rounded-[3rem] border-2 cursor-pointer transition-all duration-500 flex flex-col ${isActive ? 'border-parliament-500 bg-navy-900 shadow-[0_40px_100px_-20px_rgba(0,132,255,0.3)] scale-[1.05] z-10' : 'border-white/5 bg-white/5 backdrop-blur-sm hover:border-white/20 hover:bg-white/[0.08]'}`}
                  >
                      {plan.recommended && (
                        <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-parliament-600 to-indigo-600 text-white px-8 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-[0_10px_20px_-5px_rgba(0,132,255,0.5)] flex items-center gap-2 animate-pulse">
                          <Sparkles size={14} /> RECOMENDADO
                        </div>
                      )}
                      
                      <div className="flex justify-between items-start mb-8">
                         <h3 className="text-3xl font-black text-white tracking-tight">{plan.label}</h3>
                         <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${isActive ? 'bg-parliament-500 border-parliament-500 text-white' : 'border-white/20 group-hover:border-white/40'}`}>
                           {isActive && <Check size={18} strokeWidth={4} />}
                         </div>
                      </div>
                      
                      <div className="mb-10">
                        <div className="flex items-baseline gap-2">
                           <span className="text-2xl font-black text-slate-400">R$</span>
                           <p className="text-6xl font-black text-white leading-none">
                              {billingCycle === 'monthly' ? plan.priceM.toFixed(0) : plan.installment.toFixed(2).replace('.', ',')}
                           </p>
                           <span className="text-slate-500 font-bold">/mês</span>
                        </div>
                        
                        <div className="mt-4 space-y-1">
                           <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.15em]">
                             {billingCycle === 'monthly' ? 'PAGAMENTO MENSAL RECORRENTE' : `PAGAMENTO ÚNICO ANUAL ${plan.priceY.toFixed(0)}`}
                           </p>
                           {billingCycle === 'yearly' && (
                             <p className="text-[10px] text-emerald-400 font-black uppercase tracking-widest flex items-center gap-2">
                               <CheckCircle2 size={12} /> ATÉ 12X SEM JUROS NO CARTÃO
                             </p>
                           )}
                        </div>
                      </div>

                      <ul className="space-y-5 mb-10 flex-1 border-t border-white/10 pt-8">
                        {plan.features.map((feat, i) => (
                          <li key={i} className="flex items-center gap-4 text-sm font-bold text-slate-300">
                              <div className="w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/20">
                                <Check size={12} strokeWidth={4} />
                              </div>
                              {feat}
                          </li>
                        ))}
                      </ul>

                      <div className="pt-4">
                         <div className={`w-full py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest text-center transition-all duration-500 ${isActive ? 'bg-white text-navy-950 shadow-xl' : 'bg-white/5 text-white border border-white/10'}`}>
                            {isActive ? 'PLANO SELECIONADO' : 'SELECIONAR ESTE'}
                         </div>
                      </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center pt-4">
              <button 
                onClick={() => setStep('payment')} 
                className="px-24 py-7 bg-parliament-600 text-white rounded-[2.5rem] font-black text-sm uppercase tracking-[0.25em] shadow-[0_20px_50px_-10px_rgba(0,132,255,0.4)] hover:bg-parliament-50 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-5 group"
              >
                  PROSSEGUIR PARA CHECKOUT <ArrowRightCircle size={28} className="group-hover:translate-x-2 transition-transform" />
              </button>
            </div>
         </div>
      </div>
    );
  }

  if (step === 'create_cabinet') {
     return (
      <div className="min-h-screen bg-neutral-50 dark:bg-navy-950 flex flex-col items-center justify-center p-6 animate-in slide-in-from-right-4">
         <div className="bg-white dark:bg-navy-900 p-12 rounded-[40px] shadow-2xl max-lg w-full text-center">
            <div className="w-20 h-20 bg-navy-950 dark:bg-white text-white dark:text-navy-900 rounded-[28px] flex items-center justify-center mx-auto mb-6 shadow-lg"><Building2 size={40}/></div>
            <h2 className="text-3xl font-black text-navy-950 dark:text-white mb-8">Nome do Gabinete</h2>
            <input 
               autoFocus
               value={cabinetName} 
               onChange={e => setCabinetName(e.target.value)} 
               placeholder="Ex: Gabinete Dep. Silva" 
               className="w-full text-center text-xl font-bold bg-neutral-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-800 rounded-2xl px-6 py-5 outline-none focus:ring-4 focus:ring-parliament-100 dark:focus:ring-parliament-900 mb-8 dark:text-white"
            />
            <div className="flex gap-4">
               <button onClick={() => setStep('options')} className="flex-1 py-4 text-slate-400 font-black text-xs uppercase">Voltar</button>
               <button onClick={() => { if(cabinetName.trim()) setStep('select_plan') }} className="flex-[2] bg-navy-950 dark:bg-parliament-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg">Continuar</button>
            </div>
         </div>
      </div>
     );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-navy-950 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-4xl space-y-12 animate-in fade-in slide-in-from-bottom-4">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-black text-navy-950 dark:text-white tracking-tighter">Bem-vindo, {currentUser.name}!</h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-lg">Para começar, escolha uma das opções abaixo.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div onClick={() => setStep('create_cabinet')} className="bg-white dark:bg-navy-900 p-10 rounded-[40px] shadow-xl border border-neutral-200 dark:border-navy-800 flex flex-col h-full relative overflow-hidden group hover:border-parliament-200 transition-all cursor-pointer">
             <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity transform group-hover:scale-110"><Building2 size={120} /></div>
             <div className="w-16 h-16 bg-navy-950 dark:bg-white text-white dark:text-navy-950 rounded-[24px] flex items-center justify-center mb-6 shadow-lg"><Plus size={32} /></div>
             <h3 className="text-2xl font-black text-navy-950 dark:text-white mb-2 tracking-tight">Criar Novo Gabinete</h3>
             <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-8 leading-relaxed">Você se tornará o Administrador de uma nova instância. Ideal para parlamentares.</p>
             <button className="mt-auto w-full bg-navy-950 dark:bg-white text-white dark:text-navy-950 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg relative z-10">Iniciar Criação</button>
          </div>

          <div className="bg-white dark:bg-navy-900 p-10 rounded-[40px] shadow-xl border border-neutral-200 dark:border-navy-800 flex flex-col h-full relative overflow-hidden group hover:border-emerald-200 transition-all">
             <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-10 transition-opacity transform group-hover:scale-110"><Mail size={120} /></div>
             <div className="w-16 h-16 bg-emerald-500 text-white rounded-[24px] flex items-center justify-center mb-6 shadow-lg"><Mail size={32} /></div>
             <div className="flex justify-between items-start mb-2">
                <h3 className="text-2xl font-black text-navy-950 dark:text-white tracking-tight">Convites Pendentes</h3>
                <button onClick={(e) => { e.stopPropagation(); fetchInvitations(); }} className="p-2 bg-neutral-50 dark:bg-navy-800 rounded-full text-slate-400 hover:text-emerald-600 transition-all" disabled={checkingInvites}><RefreshCw size={16} className={checkingInvites ? 'animate-spin' : ''} /></button>
             </div>
             <div className="mt-auto space-y-4 relative z-10 flex-1 flex flex-col">
                {invitations.filter(i => i.status === 'pending').length > 0 ? (
                   <div className="space-y-3">
                      {invitations.filter(i => i.status === 'pending').map(invite => (
                         <div key={invite.id} className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/30 rounded-2xl flex items-center justify-between">
                            <div><p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Convite para {invite.role}</p><p className="text-xs font-bold text-navy-900 dark:text-white mt-0.5">Entrar no Gabinete</p></div>
                            <button onClick={() => handleAcceptInvite(invite)} disabled={loadingInvite} className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500 transition-all shadow-md">{loadingInvite ? <Loader2 className="animate-spin" size={14} /> : 'Aceitar'}</button>
                         </div>
                      ))}
                   </div>
                ) : (
                   <div className="flex-1 flex flex-col items-center justify-center text-slate-400 border-2 border-dashed border-slate-100 dark:border-navy-800 rounded-2xl p-6">
                      <Mail size={24} className="mb-2 opacity-50" /><p className="text-xs font-bold">Nenhum convite encontrado.</p>
                   </div>
                )}
             </div>
          </div>
        </div>

        <div className="text-center">
           <button onClick={handleLogout} className="text-slate-400 hover:text-red-500 font-black text-[10px] uppercase tracking-widest flex items-center gap-2 mx-auto transition-colors"><LogOut size={14} /> Sair da conta</button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
